import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuthenticatedQuery } from "@/hooks/useAuthenticatedQuery";
import { settingsApi } from "@/lib/api/typedClient";
import { PlanCoachChat } from "@/components/plan/PlanCoachChat";

interface HistorySession {
  id: string;
  date: string;
  sport: string;
  title: string;
  duration_minutes: number | null;
  distance_km: number | null;
  tss: number | null;
  planned_session_id: string | null;
  completed_activity_id: string | null;
  status: "completed" | "missed" | "skipped" | "planned";
}

interface HistoryResponse {
  sessions: HistorySession[];
  total: number;
}

const STATUS_FILTERS = ["all", "completed", "missed", "skipped"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const SPORT_ICONS: Record<string, string> = {
  run: "\u{1F3C3}",
  bike: "\u{1F6B4}",
  swim: "\u{1F3CA}",
  strength: "\u{1F4AA}",
};

const STATUS_STYLES: Record<string, string> = {
  completed: "bg-[rgba(0,255,200,0.12)] text-[#00FFC8]",
  missed: "bg-[rgba(255,80,80,0.12)] text-[#FF5050]",
  skipped: "bg-[rgba(255,180,60,0.12)] text-[#FFB43C]",
  planned: "bg-[rgba(255,255,255,0.06)] text-slate-400",
};

async function fetchSessionHistory(filter: StatusFilter): Promise<HistoryResponse> {
  const res =
    filter === "all"
      ? await settingsApi.getSessionsHistory({ limit: 200 })
      : await settingsApi.getSessionsHistory({ limit: 200, status: filter });
  return res.data;
}

/**
 * Deduplicate sessions: if a session has both planned_session_id AND completed_activity_id,
 * it's ONE session (paired). Show as single row.
 */
function deduplicateSessions(sessions: HistorySession[]): HistorySession[] {
  const seen = new Set<string>();
  return sessions.filter((s) => {
    const key = s.planned_session_id ?? s.completed_activity_id ?? s.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function History() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<StatusFilter>("all");

  const { data, isLoading, error, isError } = useAuthenticatedQuery({
    queryKey: ["sessionHistory", filter],
    queryFn: () => fetchSessionHistory(filter),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const sessions = data ? deduplicateSessions(data.sessions) : [];

  return (
    <AppLayout>
      <div className="flex flex-col h-full min-h-screen bg-transparent">
        <PageHeader
          title={t("nav.history")}
          subtitle={"training log \u00B7 session archive"}
          statusLabel="HISTORY: ACTIVE"
        />

        {/* Filter tabs */}
        <div className="flex gap-2 mt-4 mb-3 px-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest transition-colors ${
                filter === f
                  ? "bg-[rgba(0,255,200,0.12)] text-[#00FFC8] border border-[rgba(0,255,200,0.25)]"
                  : "bg-white/[0.03] text-slate-500 border border-white/[0.06] hover:bg-white/[0.06] hover:text-slate-400"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 rounded-xl border border-border/50 backdrop-blur-sm">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <div className="text-center py-12 text-muted-foreground rounded-xl border border-border/50 backdrop-blur-sm">
              <p className="text-sm font-mono">Failed to load training history</p>
              <p className="text-xs mt-2 opacity-60">
                {error instanceof Error ? error.message : String(error)}
              </p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12 text-slate-500 rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm">
              <p className="text-sm font-mono">No sessions found</p>
              <p className="text-[10px] mt-1 font-mono uppercase tracking-wider text-slate-600">
                {filter !== "all" ? `No ${filter} sessions` : "Complete a workout to see it here"}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm p-3.5 flex items-center gap-3.5 hover:bg-white/[0.04] transition-colors"
                >
                  {/* Sport icon */}
                  <div className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-base shrink-0">
                    {SPORT_ICONS[session.sport.toLowerCase()] ?? "\u{1F3CB}"}
                  </div>

                  {/* Title + meta */}
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm text-slate-200 truncate">{session.title}</p>
                    <p className="text-[10px] font-mono text-slate-500 tracking-wider mt-0.5">
                      {format(parseISO(session.date), "MMM d, yyyy")}
                      {session.duration_minutes != null && ` \u00b7 ${session.duration_minutes}min`}
                      {session.distance_km != null && ` \u00b7 ${session.distance_km.toFixed(1)}km`}
                    </p>
                  </div>

                  {/* Status badge + TSS */}
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span
                      className={`text-[9px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest ${
                        STATUS_STYLES[session.status] ?? STATUS_STYLES.planned
                      }`}
                    >
                      {session.status}
                    </span>
                    {session.tss != null && (
                      <span className="text-[10px] font-mono text-slate-500">TSS {session.tss}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Floating Coach Chat */}
        <PlanCoachChat />
      </div>
    </AppLayout>
  );
}
